import { useEffect, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/services/api';
import { resolveApiBaseUrl } from '@/utils/apiBase';

export interface Notification {
  id: number;
  type: string;
  title: string;
  message: string;
  data?: any;
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  read: boolean;
  readAt?: string;
  createdAt: string;
}

export const useNotifications = () => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const { accessToken } = useAuthStore();
  const queryClient = useQueryClient();

  // Buscar notificações
  const { data: notifications = [], refetch } = useQuery<Notification[]>({
    queryKey: ['notifications'],
    queryFn: async () => {
      const response = await api.get('/notifications');
      return response.data.data;
    },
    enabled: !!accessToken,
  });

  // Buscar contador de não lidas
  // Desabilitar polling quando websocket está conectado para evitar requisições desnecessárias
  const { data: unreadCountData } = useQuery<{ count: number }>({
    queryKey: ['notifications', 'unread-count'],
    queryFn: async () => {
      const response = await api.get('/notifications/unread/count');
      return response.data.data;
    },
    enabled: !!accessToken,
    // Apenas fazer polling se websocket não estiver conectado
    refetchInterval: socket?.connected ? false : 60000, // 60 segundos quando websocket não está disponível
  });

  useEffect(() => {
    setUnreadCount(unreadCountData?.count || 0);
  }, [unreadCountData]);

  // Conectar WebSocket
  useEffect(() => {
    if (!accessToken) {
      // Fechar socket se não houver token
      if (socket) {
        socket.close();
        setSocket(null);
      }
      return;
    }

    // Se já existe um socket conectado e válido, não criar outro
    if (socket?.connected) {
      return;
    }

    // Fechar socket anterior se existir mas não estiver conectado
    if (socket && !socket.connected) {
      socket.close();
      setSocket(null);
    }

    const apiUrl = resolveApiBaseUrl();
    const wsUrl = (() => {
      if (apiUrl.startsWith('http')) {
        return apiUrl
          .replace('/api/v1', '')
          .replace('http://', 'ws://')
          .replace('https://', 'wss://');
      }
      if (typeof window !== 'undefined') {
        return window.location.origin
          .replace('http://', 'ws://')
          .replace('https://', 'wss://');
      }
      return 'ws://localhost:4000';
    })();

    const newSocket = io(wsUrl, {
      auth: {
        token: accessToken,
      },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
      // Evitar múltiplas conexões
      forceNew: false,
      // Timeout para conexão
      timeout: 20000,
    });

    let isMounted = true;

    newSocket.on('connect', () => {
      if (isMounted) {
        console.log('Socket connected');
      }
    });

    newSocket.on('connected', (data) => {
      if (isMounted) {
        console.log('Socket authenticated', data);
      }
    });

    newSocket.on('notification', (notification: Notification) => {
      if (isMounted) {
        console.log('New notification received', notification);
        // Invalidar queries para buscar novas notificações
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
        queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
        
        // Atualizar contador
        setUnreadCount((prev) => prev + 1);

        // Tocar som para notificações importantes
        if (notification.priority === 'HIGH' || notification.priority === 'URGENT') {
          try {
            const audio = new Audio('/notification-sound.mp3');
            audio.volume = 0.5;
            audio.play().catch(() => {
              // Ignorar erros de autoplay
            });
          } catch (error) {
            // Ignorar erros
          }
        }
      }
    });

    newSocket.on('disconnect', (reason) => {
      if (isMounted) {
        console.log('Socket disconnected', reason);
        // Se desconectou por erro, não tentar reconectar imediatamente
        if (reason === 'io server disconnect') {
          // Servidor desconectou, não reconectar automaticamente
          newSocket.disconnect();
        }
      }
    });

    newSocket.on('error', (error) => {
      if (isMounted) {
        console.error('Socket error', error);
      }
    });

    newSocket.on('connect_error', (error) => {
      if (isMounted) {
        console.error('Socket connection error', error);
        // Não tentar reconectar se for erro de autenticação
        if (error.message.includes('Authentication')) {
          newSocket.disconnect();
        }
      }
    });

    if (isMounted) {
      setSocket(newSocket);
    }

    return () => {
      isMounted = false;
      if (newSocket && newSocket.connected) {
        newSocket.close();
      }
    };
  }, [accessToken, queryClient]);

  const markAsRead = useCallback(async (id: number) => {
    try {
      await api.patch(`/notifications/${id}/read`);
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    } catch (error) {
      console.error('Error marking notification as read', error);
    }
  }, [queryClient]);

  const markAllAsRead = useCallback(async () => {
    try {
      await api.post('/notifications/mark-all-read');
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    } catch (error) {
      console.error('Error marking all notifications as read', error);
    }
  }, [queryClient]);

  const deleteNotification = useCallback(async (id: number) => {
    try {
      await api.delete(`/notifications/${id}`);
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    } catch (error) {
      console.error('Error deleting notification', error);
    }
  }, [queryClient]);

  return {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refetch,
  };
};

